'use client'

import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { nhost } from '@/lib/nhost'
import type { Session, DecodedToken } from '@nhost/nhost-js/session'
import { FetchError } from '@nhost/nhost-js/fetch'
import { collectDeviceInfo } from '@/lib/device-info'
import { gqlRequest } from '@/services/graphql'
import { UpdateSessionMetadataDocument } from '@/graphql/profile.generated'
import { clearMenuCache } from '@/lib/menu/menu-cache'
import { apolloClient } from '@/lib/apollo-client'
import { decodeJwtPayload, claimsFromDecodedToken } from '@/lib/auth-claims'
import { setMonitoringIdentity } from '@/lib/monitoring/graphql-error-capture'
import { clearImpersonation } from '@/lib/impersonation'
import { browserListStateStorage, clearListState } from '@/crud/list-state'
import { useImpersonation } from '@/hooks/use-impersonation'
import { z } from 'zod'

// Cross-tab auth sync channel. The cookie session backend does NOT emit `storage` events, and each
// tab has its own Nhost client, so sign-in/out/refresh in one tab must be broadcast to the others.
const AUTH_CHANNEL = 'taskyinn-auth'

/**
 * Check if the user's account is still active. Nhost doesn't reject disabled accounts on token
 * refresh, so we verify explicitly via an admin-secret API route. That route requires the caller's
 * bearer token (it only lets a user check THEIR OWN status) — without it the route returns
 * `{disabled:false}` and the check silently never fires. If disabled, force sign-out.
 */
async function verifyAccountActive(userId: string) {
  try {
    const accessToken = nhost.getUserSession()?.accessToken
    if (!accessToken) return
    const response = await fetch(`/api/auth/check-account-status?userId=${userId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    const result = await response.json()

    if (result.disabled) {
      // Account disabled — force sign out. clearSession triggers onChange → clears state → the
      // proxy/ProtectedRoute redirect to /login.
      await nhost.auth.signOut({}).catch(() => {})
      clearListState(browserListStateStorage())
      nhost.clearSession()
    }
  } catch (error) {
    // Network error — don't sign out, user might be offline
    console.warn('[Auth] Could not verify account status:', error)
  }
}

// Zod schema for sign-in response with MFA
const SignInResponseSchema = z.object({
  mfa: z.object({ ticket: z.string() }).optional(),
  session: z.unknown().optional(),
})

export interface MfaChallenge {
  ticket: string
}

/**
 * Update session with device info after login. Runs in the background — does not block login.
 * The session is fully established once signInEmailPassword/verifySignInMfaTotp resolve, so we read
 * it synchronously (no timing hack).
 *
 * ⛔ #1647: `suppressToast` is load-bearing, not tidiness. `auth.refresh_tokens`
 * grants UPDATE to eleven roles; kitchen_staff, cashier, server, outlet_manager,
 * pos_admin, pos_reporting, accountant and property_admin hold none, so for
 * eight roles this optional telemetry write greeted the user with a red
 * "Request blocked" toast at the moment of login. The catch below already says
 * this is best-effort — the toast simply contradicted the code. The fix is to
 * stop shouting about it, NOT to grant eight roles write access to an auth
 * table so a device-info nicety can succeed.
 */
function updateSessionWithDeviceInfo() {
  void (async () => {
    try {
      const session = nhost.getUserSession()
      const refreshTokenId = session?.refreshTokenId
      if (!refreshTokenId) return

      // Collect device info (includes async IP lookup)
      const deviceInfo = await collectDeviceInfo()

      await gqlRequest(UpdateSessionMetadataDocument, {
        id: refreshTokenId,
        metadata: {
          browser: deviceInfo.browser,
          browserVersion: deviceInfo.browserVersion,
          os: deviceInfo.os,
          osVersion: deviceInfo.osVersion,
          device: deviceInfo.device,
          deviceType: deviceInfo.deviceType,
          screenResolution: deviceInfo.screenResolution,
          language: deviceInfo.language,
          timezone: deviceInfo.timezone,
          ip: deviceInfo.ip,
          city: deviceInfo.city,
          country: deviceInfo.country,
          countryCode: deviceInfo.countryCode,
          lastActive: new Date().toISOString(),
        },
      }, { suppressToast: true })
    } catch {
      // Silently fail - device info is optional enhancement
    }
  })()
}

/**
 * Invalidate all other sessions for the user (single session enforcement).
 * Awaited during login to ensure other devices are logged out before proceeding.
 */
async function invalidateOtherSessions(refreshTokenId: string, accessToken: string) {
  try {
    const response = await fetch('/api/auth/invalidate-other-sessions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ currentRefreshTokenId: refreshTokenId }),
    })

    if (!response.ok) {
      console.warn('[Auth] Failed to invalidate other sessions:', response.status)
    }
  } catch (error) {
    // Best-effort — don't block login if this fails
    console.warn('[Auth] Error invalidating other sessions:', error)
  }
}

interface AuthContextType {
  user: Session['user'] | null
  session: Session | null
  loading: boolean
  signIn: (
    email: string,
    password: string
  ) => Promise<{
    success: boolean
    response?: unknown
    error?: { message: string }
    mfa?: MfaChallenge
  }>
  verifyMfaTotp: (
    ticket: string,
    otp: string
  ) => Promise<{
    success: boolean
    error?: { message: string }
  }>
  signUp: (
    email: string,
    password: string
  ) => Promise<{
    success: boolean
    response?: unknown
    error?: { message: string }
    needsVerification?: boolean
  }>
  signOut: () => Promise<void>
  refreshSession: () => Promise<void>
  sendPasswordResetEmail: (
    email: string,
    options?: { redirectTo?: string }
  ) => Promise<{
    success: boolean
    error?: { message: string }
  }>
  sendMagicLink: (
    email: string,
    options?: { redirectTo?: string }
  ) => Promise<{
    success: boolean
    error?: { message: string }
  }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({
  children,
  initialSession = null,
}: {
  children: React.ReactNode
  // Seeded server-side (RSC layout via requireDashboard) so the first paint is auth-correct — no
  // loading flash. The bootstrap effect below still reconciles from the live cookie session.
  initialSession?: Session | null
}) {
  const [user, setUser] = useState<Session['user'] | null>(initialSession?.user ?? null)
  const [session, setSession] = useState<Session | null>(initialSession)
  const [loading, setLoading] = useState(!initialSession)
  const channelRef = useRef<BroadcastChannel | null>(null)
  const impersonation = useImpersonation()

  // Publish local auth state (session or null) into React state.
  const applySession = useCallback((next: Session | null) => {
    setSession(next)
    setUser(next?.user ?? null)
    // Error-monitoring identity: every GlitchTip event carries user/role/
    // tenant/property tags so permission failures are attributable.
    const claims = claimsFromDecodedToken(next?.decodedToken as Record<string, unknown> | undefined)
    setMonitoringIdentity(
      next?.user
        ? {
            userId: next.user.id,
            role: claims?.role,
            tenantId: claims?.tenantId,
            activePropertyId: claims?.activePropertyId,
          }
        : null
    )
  }, [])

  // Tell other tabs to re-read their session (cookies don't fire storage events).
  const announce = useCallback(() => channelRef.current?.postMessage('changed'), [])

  useEffect(() => {
    // Handle a redirect-based callback (Nhost magic link / passwordless) that returns a refreshToken
    // in the URL hash or query — exchange it for a session, then clean the URL.
    const handleOAuthCallback = async () => {
      const hash = window.location.hash
      const params = new URLSearchParams(window.location.search)

      let refreshToken: string | null = null
      if (hash) {
        refreshToken = new URLSearchParams(hash.substring(1)).get('refreshToken')
      }
      if (!refreshToken) {
        refreshToken = params.get('refreshToken')
      }

      if (refreshToken) {
        try {
          await nhost.auth.refreshToken({ refreshToken })
          window.history.replaceState({}, '', window.location.pathname)
        } catch (error) {
          console.error('[Auth] Magic-link callback error:', error)
          window.history.replaceState({}, '', window.location.pathname)
        }
      }
    }

    // Bootstrap: use the existing session if present, otherwise try a redirect callback.
    const initialSession = nhost.getUserSession()
    if (initialSession) {
      applySession(initialSession)
      setLoading(false)
      // Verify the account is still active (Nhost doesn't reject disabled accounts on refresh).
      if (initialSession.user?.id) verifyAccountActive(initialSession.user.id)
    } else {
      handleOAuthCallback().finally(() => {
        applySession(nhost.getUserSession())
        setLoading(false)
      })
    }

    // In-tab: re-read whenever the SDK rewrites the session (sign-in/out/refresh).
    const unsubscribe = nhost.sessionStorage.onChange(applySession)

    // Cross-tab: propagate sign-in/out/refresh over a BroadcastChannel; also re-read on tab focus.
    let channel: BroadcastChannel | null = null
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(AUTH_CHANNEL)
      channel.onmessage = () => applySession(nhost.getUserSession())
      channelRef.current = channel
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') applySession(nhost.getUserSession())
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      unsubscribe()
      channel?.close()
      channelRef.current = null
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [applySession])

  const signIn = async (email: string, password: string) => {
    try {
      const response = await nhost.auth.signInEmailPassword({ email, password })

      // Check if MFA is required (using Zod validation instead of type assertion)
      const parsed = SignInResponseSchema.safeParse(response.body)
      if (parsed.success && parsed.data.mfa?.ticket) {
        return {
          success: false,
          mfa: { ticket: parsed.data.mfa.ticket },
          response
        }
      }

      // Get session info for single session enforcement
      const session = nhost.getUserSession()
      if (session?.refreshTokenId && session?.accessToken) {
        await invalidateOtherSessions(session.refreshTokenId, session.accessToken)
      }

      // Update session with device info (async, don't wait)
      updateSessionWithDeviceInfo()
      announce()

      return { success: true, response }
    } catch (error) {
      // Handle FetchError as per Nhost documentation
      if (error instanceof FetchError) {
        const serverMessage = error.body?.message || ''
        // Check for disabled account — show specific message
        let errorMessage: string
        if (serverMessage.toLowerCase().includes('disabled')) {
          errorMessage = 'Your account has been disabled. Please contact your administrator.'
        } else {
          // Generic message to prevent user enumeration attacks
          errorMessage = 'Invalid email or password. Please try again.'
        }

        return {
          success: false,
          error: {
            message: errorMessage,
          },
        }
      }

      // Re-throw if it's not a FetchError (unexpected error)
      throw error
    }
  }

  const verifyMfaTotp = async (ticket: string, otp: string) => {
    try {
      await nhost.auth.verifySignInMfaTotp({ ticket, otp })

      // Get session info for single session enforcement
      const session = nhost.getUserSession()
      if (session?.refreshTokenId && session?.accessToken) {
        await invalidateOtherSessions(session.refreshTokenId, session.accessToken)
      }

      // Update session with device info (async, don't wait)
      updateSessionWithDeviceInfo()
      announce()

      return { success: true }
    } catch (error) {
      if (error instanceof FetchError) {
        let errorMessage = 'Invalid verification code'

        if (error.body?.message) {
          if (error.body.message.includes('Invalid') || error.body.message.includes('invalid')) {
            errorMessage = 'Invalid verification code. Please try again.'
          } else if (error.body.message.includes('expired')) {
            errorMessage = 'Verification code expired. Please sign in again.'
          } else {
            errorMessage = error.body.message
          }
        }

        return {
          success: false,
          error: { message: errorMessage },
        }
      }

      throw error
    }
  }

  const signUp = async (email: string, password: string) => {
    try {
      const response = await nhost.auth.signUpEmailPassword({ email, password })

      // Check if user was automatically signed in (no email verification required)
      if (response.body?.session) {
        announce()
        return { success: true, response, needsVerification: false }
      } else {
        return { success: true, response, needsVerification: true }
      }
    } catch (error) {
      // Handle FetchError as per Nhost documentation
      if (error instanceof FetchError) {
        let errorMessage = 'Sign up failed'

        // Access error details from error.body as per Nhost docs
        if (error.body?.message) {
          if (error.body.message.includes('Email already in use')) {
            errorMessage =
              'This email is already registered. Please try signing in instead.'
          } else if (error.body.message.includes('Invalid email')) {
            errorMessage = 'Please enter a valid email address.'
          } else if (error.body.message.includes('Password')) {
            errorMessage = 'Password does not meet requirements.'
          } else {
            errorMessage = error.body.message
          }
        } else if (error.message) {
          errorMessage = error.message
        }

        return {
          success: false,
          error: {
            message: errorMessage,
          },
        }
      }

      // Re-throw if it's not a FetchError (unexpected error)
      throw error
    }
  }

  const signOut = async () => {
    try {
      const currentSession = nhost.getUserSession()
      if (currentSession?.refreshToken) {
        await nhost.auth.signOut({ refreshToken: currentSession.refreshToken })
      }
    } catch (error) {
      console.error('[Auth] Sign out failed:', error)
      // Still clear local state even if server call fails
    } finally {
      // Never leave an impersonation overlay dangling after sign-out.
      clearImpersonation()
      // Saved list filters / searches / back links hold guest names — never the next user's (TKT-20261018-0003).
      clearListState(browserListStateStorage())
      // Always clear the session + local state.
      nhost.clearSession()
      applySession(null)
      // Clear menu cache to prevent stale data on next login
      clearMenuCache()
      // Clear Apollo cache to prevent stale/sensitive data leakage
      try {
        await apolloClient?.clearStore()
      } catch (cacheError) {
        // Ignore cache clear errors (e.g., "Store reset while query was in flight")
        console.warn('[Auth] Apollo cache clear warning:', cacheError)
      }
      announce()
    }
  }

  const refreshSession = async () => {
    // Force a re-mint (margin 0) so callers that changed claims/metadata (role grant, tenant/property
    // switch, profile update) immediately see the new token; then let other tabs pick it up.
    await nhost.refreshSession(0)
    announce()
  }

  const sendPasswordResetEmail = async (
    email: string,
    options?: { redirectTo?: string }
  ) => {
    try {
      const body: { email: string; options?: { redirectTo?: string } } = { email }
      if (options?.redirectTo) {
        body.options = { redirectTo: options.redirectTo }
      }
      await nhost.auth.sendPasswordResetEmail(body)
      return { success: true }
    } catch (error) {
      if (error instanceof FetchError) {
        let errorMessage = 'Failed to send password reset email'

        if (error.body?.message) {
          if (error.body.message.includes('Invalid email')) {
            errorMessage = 'Please enter a valid email address.'
          } else if (error.body.message.includes('not found')) {
            errorMessage = 'No account found with this email address.'
          } else if (error.body.message.includes('redirectTo-not-allowed')) {
            errorMessage = 'Password reset email sent. Please check your inbox.'
            // Still return success since the email was sent
            return { success: true }
          } else {
            errorMessage = error.body.message
          }
        } else if (error.message) {
          errorMessage = error.message
        }

        return {
          success: false,
          error: {
            message: errorMessage,
          },
        }
      }

      throw error
    }
  }

  const sendMagicLink = async (
    email: string,
    options?: { redirectTo?: string }
  ) => {
    try {
      const body: { email: string; options?: { redirectTo?: string } } = { email }
      if (options?.redirectTo) {
        body.options = { redirectTo: options.redirectTo }
      }
      await nhost.auth.signInPasswordlessEmail(body)
      return { success: true }
    } catch (error) {
      if (error instanceof FetchError) {
        let errorMessage = 'Failed to send magic link'

        if (error.body?.message) {
          if (error.body.message.includes('Invalid email')) {
            errorMessage = 'Please enter a valid email address.'
          } else if (error.body.message.includes('disabled')) {
            errorMessage = 'Magic link signin is currently disabled.'
          } else if (error.body.message.includes('redirectTo-not-allowed')) {
            errorMessage = 'Magic link sent! Please check your inbox.'
            // Still return success since the email was sent
            return { success: true }
          } else {
            errorMessage = error.body.message
          }
        } else if (error.message) {
          errorMessage = error.message
        }

        return {
          success: false,
          error: {
            message: errorMessage,
          },
        }
      }

      throw error
    }
  }

  // While impersonating, the effective identity for this tab is the acted-as user (from the per-tab
  // overlay), NOT the admin's real cookie session. Derive a Session-shaped view so every consumer of
  // useAuth() (chrome, guards) reflects the impersonated user; the real session stays untouched.
  const effectiveUser = useMemo<Session['user'] | null>(
    () => (impersonation ? (impersonation.user as unknown as Session['user']) : user),
    [impersonation, user],
  )
  const effectiveSession = useMemo<Session | null>(() => {
    if (!impersonation) return session
    return {
      accessToken: impersonation.accessToken,
      accessTokenExpiresIn: 0,
      refreshToken: '',
      refreshTokenId: '',
      user: impersonation.user as unknown as Session['user'],
      decodedToken: (decodeJwtPayload(impersonation.accessToken) ?? {}) as DecodedToken,
    } as Session
  }, [impersonation, session])

  const value = {
    user: effectiveUser,
    session: effectiveSession,
    loading,
    signIn,
    verifyMfaTotp,
    signUp,
    signOut,
    refreshSession,
    sendPasswordResetEmail,
    sendMagicLink,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
